import {
  Button,
  Checkbox,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Progress,
} from '@heroui/react';
import { Copy } from 'lucide-react';
import React, { FC, useState } from 'react';

import { useHeroToast } from '../../../../hooks';
import { AdCreativeListItem, useFetchAdCreative, usePostAdCreative } from '../../../../queries';
import { AdFormat, findFormat, findPlatform, PLATFORMS } from '../../config/platforms';
import { useSceneCapture } from '../../hooks';
import { uploadToBytescale } from '../../utils/bytescale';
import { adaptSceneToFormat } from '../../utils/formats';
import { fromSavedCreative, toSavedScene } from '../../utils/scene';
import { CardHint, CardTitle } from './PlatformStep.styled';

interface AdaptFormatsModalProps {
  creative: AdCreativeListItem;
  onClose: () => void;
}

/** Dłuższy bok miniatury zapisywanej razem z każdą nową kreacją. */
const PREVIEW_MAX_SIZE = 480;

const formatKey = (platformId: string, format: AdFormat): string => `${platformId}::${format.id}`;

/**
 * Nazwa własna kreacji, bez prefiksu z platformą i formatem.
 *
 * Domyślna nazwa przy zapisie to „Platforma Format", więc przeniesiona kreacja
 * dziedziczyłaby platformę źródłową: post z Facebooka nazywałby się na
 * LinkedInie „Facebook Post kwadratowy (Post z linkiem)". Odcinamy ten prefiks
 * i budujemy nazwę od platformy docelowej.
 */
const ownName = (creative: AdCreativeListItem): string => {
  const platform = findPlatform(creative.platformId)?.name ?? '';
  const format = findFormat(creative.platformId, creative.formatId)?.label ?? '';
  const prefix = `${platform} ${format}`.trim();

  const stripped = prefix && creative.name.startsWith(prefix)
    ? creative.name.slice(prefix.length)
    : creative.name;

  return stripped.replace(/^[\s,()-]+/, '').replace(/[\s()-]+$/, '').trim();
};

/**
 * Przenosi jedną kreację na wybrane formaty.
 *
 * Każdy format zapisujemy jako OSOBNĄ kreację, a nie wariant jednej: formaty
 * różnią się na tyle, że po przeskalowaniu prawie zawsze poprawia się je
 * ręcznie, a wtedy wspólny dokument byłby więzieniem, nie ułatwieniem.
 */
export const AdaptFormatsModal: FC<AdaptFormatsModalProps> = ({ creative, onClose }) => {
  const fetchCreative = useFetchAdCreative();
  const { mutateAsync: create } = usePostAdCreative();
  const capture = useSceneCapture();
  const { showError, showSuccess } = useHeroToast();

  const [selected, setSelected] = useState<string[]>([]);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const toggle = (key: string): void => {
    setSelected((current) => (
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key]
    ));
  };

  const run = async (): Promise<void> => {
    if (selected.length === 0) return;

    setProgress({ done: 0, total: selected.length });

    try {
      const details = await fetchCreative(creative.id);
      const source = fromSavedCreative(details);
      const base = ownName(creative);

      // Po kolei, nie równolegle: każdy format renderuje własną miniaturę na
      // tej samej ukrytej planszy, więc równoległość nadpisywałaby scenę.
      for (const key of selected) {
        const [platformId, formatId] = key.split('::');
        const platform = PLATFORMS.find((item) => item.id === platformId);
        const format = platform?.formats.find((item) => item.id === formatId);
        if (!platform || !format) continue;

        const adapted = adaptSceneToFormat(source, platformId, format);
        const name = [`${platform.name} ${format.label}`, base].filter(Boolean).join(' - ');

        let preview = '';
        try {
          // eslint-disable-next-line no-await-in-loop
          const blob = await capture.capture(adapted, PREVIEW_MAX_SIZE);
          if (blob) {
            // eslint-disable-next-line no-await-in-loop
            const uploaded = await uploadToBytescale(blob, `${name}-${format.id}.jpg`);
            preview = uploaded.url;
          }
        } catch {
          // Brak miniatury nie może zablokować zapisu samej kreacji.
        }

        // eslint-disable-next-line no-await-in-loop
        await create({
          data: {
            name,
            kind: 'creative',
            platformId,
            formatId: format.id,
            width: format.width,
            height: format.height,
            scene: toSavedScene(adapted),
            ...(preview ? { preview } : {}),
          },
        });

        setProgress((current) => (current ? { ...current, done: current.done + 1 } : current));
      }

      showSuccess(`Utworzono ${selected.length} nowych kreacji`);
      onClose();
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Nie udało się przeliczyć kreacji');
    } finally {
      setProgress(null);
    }
  };

  const isBusy = progress !== null;

  return (
    <Modal isOpen size="2xl" radius="sm" onClose={isBusy ? undefined : onClose}>
      <ModalContent>
        <ModalHeader>{`Przelicz „${creative.name}" na inne formaty`}</ModalHeader>
        <ModalBody>
          <p className="text-xs text-gray-500 leading-relaxed">
            Elementy przeskalują się proporcjonalnie i wyśrodkują. Przy dużej zmianie proporcji,
            na przykład z kwadratu na baner, wynik traktuj jako punkt wyjścia do poprawek.
          </p>

          <div className="grid grid-cols-2 gap-4">
            {PLATFORMS.map((platform) => (
              <div key={platform.id}>
                <CardTitle>{platform.name}</CardTitle>
                <div className="mt-1 flex flex-col gap-1">
                  {platform.formats.map((format) => {
                    const key = formatKey(platform.id, format);
                    const isSource = platform.id === creative.platformId && format.id === creative.formatId;

                    return (
                      <Checkbox
                        key={key}
                        size="sm"
                        isDisabled={isSource || isBusy}
                        isSelected={selected.includes(key)}
                        onValueChange={() => toggle(key)}
                      >
                        <span className="text-xs">
                          {format.label}
                          <CardHint>{isSource ? 'format źródłowy' : `${format.width} x ${format.height} px`}</CardHint>
                        </span>
                      </Checkbox>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {progress && (
            <Progress
              size="sm"
              radius="sm"
              aria-label="Postęp przeliczania"
              label={`Tworzę kreacje: ${progress.done} z ${progress.total}`}
              value={progress.done}
              maxValue={progress.total}
              classNames={{ label: 'text-xs text-gray-500' }}
            />
          )}
        </ModalBody>
        <ModalFooter>
          <Button size="sm" radius="sm" variant="light" isDisabled={isBusy} onPress={onClose}>
            Anuluj
          </Button>
          <Button
            size="sm"
            radius="sm"
            className="bg-black text-white"
            startContent={<Copy size={16} />}
            isLoading={isBusy}
            isDisabled={selected.length === 0}
            onPress={run}
          >
            {`Utwórz ${selected.length || ''} kreacji`.trim()}
          </Button>
        </ModalFooter>
      </ModalContent>

      {capture.stage}
    </Modal>
  );
};
